import { isProfileIncomplete } from '../selectors/userSelectors';

export const EMAIL_VERIFICATION_PATH = '/profile/email-verification';
export const PROFILE_COMPLETION_PATH = '/profile/edit';
export const HOME_PATH = '/';
export const LOGIN_PATH = '/login';

const SAFE_INTERNAL_RETURN_MAX_LENGTH = 512;

/**
 * Allowlisted authenticated product pathnames for post-login `next`.
 * Public `/login` and `/p/:token` are excluded (not product-return targets).
 */
const SAFE_INTERNAL_PATH_PATTERNS = [
  /^\/$/,
  /^\/profile$/,
  /^\/profile\/edit$/,
  /^\/profile\/change-password$/,
  /^\/profile\/billing$/,
  /^\/feed$/,
  /^\/search$/,
  /^\/map$/,
  /^\/saved$/,
  /^\/add$/,
  /^\/realty\/\d+$/,
  /^\/realty\/\d+\/edit$/,
  /^\/users$/,
  /^\/user\/[A-Za-z0-9._-]+$/,
  /^\/dashboard$/,
  /^\/notifications$/,
  /^\/notifications\/preferences$/,
  /^\/companies\/mine$/,
  /^\/companies\/new$/,
  /^\/companies\/find$/,
  /^\/companies\/join-request$/,
  /^\/companies\/join-request\/new-client$/,
  /^\/companies\/my-invitations$/,
  /^\/companies\/my-join-requests$/,
  /^\/companies\/invitations\/by-token\/[A-Za-z0-9-]+$/,
  /^\/companies\/\d+$/,
  /^\/companies\/\d+\/settings$/,
  /^\/companies\/\d+\/members$/,
  /^\/companies\/\d+\/invitations$/,
  /^\/companies\/\d+\/join-requests$/,
  /^\/companies\/\d+\/clients$/,
  /^\/companies\/\d+\/clients\/unassigned$/,
  /^\/companies\/\d+\/clients\/import$/,
  /^\/companies\/\d+\/clients\/\d+$/,
  /^\/companies\/\d+\/properties$/,
  /^\/companies\/\d+\/properties\/unassigned$/,
  /^\/companies\/\d+\/properties\/new$/,
  /^\/companies\/\d+\/properties\/import$/,
  /^\/companies\/\d+\/properties\/\d+$/,
  /^\/companies\/\d+\/properties\/\d+\/edit$/,
  /^\/companies\/\d+\/tasks$/,
  /^\/companies\/\d+\/tasks\/calendar$/,
  /^\/companies\/\d+\/tasks\/\d+$/,
  /^\/companies\/\d+\/viewings\/\d+$/,
  /^\/companies\/\d+\/contact\/edit$/,
  /^\/companies\/\d+\/invitations\/new$/,
  /^\/clients$/,
  /^\/clients\/\d+$/,
  /^\/clients\/\d+\/edit$/,
  /^\/clients\/\d+\/contacts\/edit$/,
  /^\/clients\/\d+\/activity$/,
  /^\/deals$/,
  /^\/deals\/\d+$/,
  /^\/finance$/,
  /^\/tasks$/,
  /^\/tasks\/calendar$/,
  /^\/chats$/,
  /^\/chats\/\d+$/,
  /^\/payment\/result$/,
];

function failsStructuralReturnSafety(trimmed) {
  if (!trimmed.startsWith('/')) return true;
  if (trimmed.startsWith('//')) return true;
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed)) return true;
  if (trimmed.includes('\\') || trimmed.includes('\0')) return true;
  if (trimmed.includes('#')) return true;
  return false;
}

function normalizePathname(pathname) {
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.replace(/\/+$/, '') || '/';
  }
  return pathname;
}

function isSafeInternalSearch(search) {
  if (!search) return true;
  if (search[0] !== '?') return false;
  if (search.includes('#') || search.includes('\\') || search.includes('\0')) {
    return false;
  }
  if (search.includes('//')) return false;
  if (search.indexOf('?', 1) !== -1) return false;
  return true;
}

function pathnameIsAllowlisted(pathname) {
  return SAFE_INTERNAL_PATH_PATTERNS.some((pattern) => pattern.test(pathname));
}

/**
 * Canonical onboarding destination for an authenticated userInfo blob.
 *
 * Priority (AUTH-ONBOARDING-EMAIL-VERIFICATION-A1):
 *   1. unverified email → /profile/email-verification
 *   2. verified + incomplete profile → /profile/edit
 *   3. verified + complete profile → null (no forced onboarding redirect)
 *
 * Unauthenticated callers (null/undefined userInfo) return null — the login
 * shell owns unauthenticated routing.
 */
export function resolveOnboardingDestination(userInfo) {
  if (!userInfo) return null;

  if (userInfo.is_email_verified !== true) {
    return EMAIL_VERIFICATION_PATH;
  }

  if (isProfileIncomplete(userInfo)) {
    return PROFILE_COMPLETION_PATH;
  }

  return null;
}

/**
 * Narrow same-origin internal return target for Property detail only
 * (REALTY-PRESENTATION-CONTACTS-A1).
 *
 * Accepts only `/realty/:id` (numeric id, optional trailing slash).
 * Rejects absolute URLs, protocol-relative URLs, schemes, and other paths.
 * Returns a normalized `/realty/:id` path or null.
 */
export function validateInternalRealtyReturnTarget(raw) {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (!trimmed.startsWith('/')) return null;
  if (trimmed.startsWith('//')) return null;
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed)) return null;
  if (trimmed.includes('\\') || trimmed.includes('\0')) return null;
  // Disallow query/hash/open-redirect tricks on the return path itself.
  if (trimmed.includes('?') || trimmed.includes('#')) return null;

  const match = /^\/realty\/(\d+)\/?$/.exec(trimmed);
  if (!match) return null;
  return `/realty/${match[1]}`;
}

/**
 * Allowlisted same-origin internal return target for B2 product routes.
 *
 * Extends the realty-only validator: structural open-redirect checks stay
 * identical, then pathname must match an explicit product allowlist.
 * Optional query is preserved when it is itself structurally safe.
 */
export function validateSafeInternalReturnTarget(raw) {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (trimmed.length > SAFE_INTERNAL_RETURN_MAX_LENGTH) return null;
  if (failsStructuralReturnSafety(trimmed)) return null;

  const qIdx = trimmed.indexOf('?');
  const rawPath = qIdx === -1 ? trimmed : trimmed.slice(0, qIdx);
  const search = qIdx === -1 ? '' : trimmed.slice(qIdx);
  if (!isSafeInternalSearch(search)) return null;

  let decodedPath;
  try {
    decodedPath = decodeURIComponent(rawPath);
  } catch (_err) {
    return null;
  }
  if (
    decodedPath.includes('..')
    || decodedPath.includes('\\')
    || decodedPath.includes('//')
    || decodedPath.includes('\0')
  ) {
    return null;
  }

  const pathname = normalizePathname(decodedPath);
  if (!pathnameIsAllowlisted(pathname)) return null;
  return `${pathname}${search}`;
}

/**
 * Anonymous private-route bounce: `/login` or `/login?next=<safe target>`.
 */
export function buildAnonymousLoginRedirect(location) {
  const pathname = typeof location?.pathname === 'string' ? location.pathname : '';
  const search = typeof location?.search === 'string' ? location.search : '';
  if (!pathname || pathname === LOGIN_PATH) return LOGIN_PATH;
  const safe = validateSafeInternalReturnTarget(`${pathname}${search}`);
  if (!safe) return LOGIN_PATH;
  return `${LOGIN_PATH}?next=${encodeURIComponent(safe)}`;
}

/**
 * Post-auth navigation target shared by LoginScreen (login / register / Google).
 *
 * Onboarding always wins. When no forced onboarding step applies, an optional
 * validated internal product return target may be used; otherwise home.
 *
 * @param {object|null|undefined} userInfo
 * @param {{ returnTarget?: string|null }} [options]
 */
export function resolvePostAuthDestination(userInfo, options = {}) {
  const onboarding = resolveOnboardingDestination(userInfo);
  if (onboarding) return onboarding;

  const safeReturn = validateSafeInternalReturnTarget(options.returnTarget);
  if (safeReturn) return safeReturn;

  return HOME_PATH;
}

/**
 * Destination after email is already verified (or just became verified).
 * Incomplete profiles continue to profile completion; otherwise home.
 */
export function resolvePostVerificationDestination(userInfo) {
  if (!userInfo) return '/login';
  if (isProfileIncomplete(userInfo)) return PROFILE_COMPLETION_PATH;
  return HOME_PATH;
}
